export type DateFormatType = "yyyy-MM-dd" | "yyyy/MM/dd";

/**
 * 指定フォーマットの文字列日付を year/month/day の数値配列へ変換する。
 * - 受け付けるフォーマットは DateFormatType で明示した2種類のみ。
 * - フォーマット不一致や数値化不可の場合は null を返す。
 */
const getDateParts = (
  dateString: string,
  dateFormat: DateFormatType
): [number, number, number] | null => {
  /** フォーマットに応じて厳密な正規表現を切り替える。 */
  const regex =
    dateFormat === "yyyy-MM-dd" ? /^(\d{4})-(\d{2})-(\d{2})$/ : /^(\d{4})\/(\d{2})\/(\d{2})$/;
  const matched = dateString.match(regex);
  if (!matched) {
    return null;
  }

  /** キャプチャした文字列を数値へ変換する。 */
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);

  /** Number 変換後に整数でなければ異常入力とみなす。 */
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  return [year, month, day];
};

/**
 * 指定タイムゾーンのUTCオフセット(ミリ秒)を取得する。
 * - 例: GMT+9 は +32400000ms
 * - shortOffset が取得できない環境/形式の場合は 0 を返す。
 */
const getTimeZoneOffsetMs = (timeZone: string, date: Date): number => {
  /** Intl.DateTimeFormat の shortOffset から GMT±HH[:mm] 形式を取得する。 */
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const zoneName = formatter
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;

  if (!zoneName) {
    return 0;
  }

  /** GMT+9, GMT+09, GMT+09:30 のような形式を許容してパースする。 */
  const matched = zoneName.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!matched) {
    return 0;
  }

  /** 符号・時・分をミリ秒へ変換する。 */
  const sign = matched[1] === "-" ? -1 : 1;
  const hours = Number(matched[2]);
  const minutes = Number(matched[3] ?? "0");
  return sign * (hours * 60 + minutes) * 60 * 1000;
};

/**
 * String形式の日付を、指定タイムゾーンの「その日の 00:00:00」を表す Date に変換する。
 * - dateString は dateFormat に一致している必要がある。
 * - 戻り値は Date。変換できない場合は null。
 */
export const convertStringToDate = (
  dateString: string,
  timeZone: string,
  dateFormat: DateFormatType
): Date | null => {
  /** まず入力文字列を数値の日付要素へ分解する。 */
  const parts = getDateParts(dateString, dateFormat);
  if (!parts) {
    return null;
  }

  const [year, month, day] = parts;

  /** 一旦 UTC の同日 00:00:00 を基準時刻として作る。 */
  const utcBase = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));

  /** 日付の繰り上がり/繰り下がりが起きた場合は存在しない日付として扱う。 */
  if (
    utcBase.getUTCFullYear() !== year ||
    utcBase.getUTCMonth() !== month - 1 ||
    utcBase.getUTCDate() !== day
  ) {
    return null;
  }

  /** 対象タイムゾーンのオフセットを差し引き、タイムゾーン基準の実時刻へ寄せる。 */
  const offsetMs = getTimeZoneOffsetMs(timeZone, utcBase);
  const converted = new Date(utcBase.getTime() - offsetMs);

  /** Date の内部値が不正なら null を返す。 */
  if (Number.isNaN(converted.getTime())) {
    return null;
  }

  return converted;
};

/**
 * Dateを指定タイムゾーン・指定フォーマットの文字列へ変換する。
 * - 出力は yyyy-MM-dd または yyyy/MM/dd
 * - 必要なパーツが取得できない場合は空文字を返す。
 */
export const formatDateByTimeZone = (
  date: Date,
  timeZone: string,
  dateFormat: DateFormatType
): string => {
  /** en-CA を使い、年/月/日を安定して 2 桁形式で取得する。 */
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  /** いずれかが欠ける場合はフォーマット不能として空文字を返す。 */
  if (!year || !month || !day) {
    return "";
  }

  /** 指定フォーマットに合わせて区切り文字を切り替える。 */
  if (dateFormat === "yyyy/MM/dd") {
    return `${year}/${month}/${day}`;
  }

  return `${year}-${month}-${day}`;
};

/**
 * ISODateTime文字列を指定タイムゾーンで "yyyy/MM/dd HH:mm" 形式にフォーマットする。
 * en-CAロケールの出力 "YYYY-MM-DD, HH:mm" を "YYYY/MM/DD HH:mm" に変換する。変換失敗時は "なし" を返す。
 */
export const formatIsoDateTimeByTimeZone = (isoString: string, timeZone: string): string => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "なし";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/,\s*/, " ")
    .replace(/-/g, "/");
};
